import React, { useState, useEffect } from "react";
import { Loader2, XCircle } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import API_CONFIG from "../../lib/ApiConfig";
import { buildFlowUrl } from "../../utils/FlowUrlBuilder";
import { OwnliButton } from "../ownli/OwnliScreenWrapper";

const RefinancingOfferCheckScreen = ({ surveyAnswers, onContinue }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasOffers, setHasOffers] = useState(false);
  const [error, setError] = useState(null);
  const { getIdToken, user } = useAuth();

  useEffect(() => {
    const checkOffers = async () => {
      try {
        if (!user) {
          return;
        }
        // Get user token
        const token = await getIdToken();
        if (token) {
          // Construct URL for checking offers
          const url = `${API_CONFIG.BASE_URL}/api/refinancing/offers`;

          // Fetch offers
          const response = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-user-token": token,
              "Access-Control-Allow-Origin": "*",
              "Access-Control-Allow-Headers":
                "Origin, X-Requested-With, Content-Type, Accept, x-user-token",
            },
          });

          // Check if response is successful
          if (!response.ok) {
            throw new Error("Failed to fetch offers");
          }

          // Parse response
          const data = await response.json();

          // Determine if offers exist (adjust logic as per your API response structure)
          const offersExist = data && data.length > 0;

          setHasOffers(offersExist);

          // If offers exist, automatically continue
          if (offersExist) {
            onContinue(data);
          }

          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error checking offers:", err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    checkOffers();
  }, [getIdToken, user]);

  // If loading, show a basic loader
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
        <p className="text-lg text-gray-600">
          Checking your refinancing options...
        </p>
      </div>
    );
  }

  // If there's an error, show error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <XCircle className="h-12 w-12 text-red-500" />
        <p className="text-lg text-red-600">
          Unable to check offers. Please try again.
        </p>
      </div>
    );
  }

  // If no offers, show the image with links to other offers
  return (
    <div className="flex flex-col items-center justify-start h-screen p-4 pt-12">
      <img
        src="https://parked.blob.core.windows.net/ownli/app-resources%5CChatGPT_Image_Dec_1%2C_2025%2C_04_31_16_PM.png"
        alt="No Offers Available"
        className="mx-auto mb-8 max-w-full max-h-90 object-contain rounded-lg shadow-lg"
      />
      <div className="w-full max-w-sm space-y-4">
        <p className="text-lg text-gray-600 text-center mb-2">
          Check out other ways to save:
        </p>
        <OwnliButton
          variant="primary"
          onClick={() => (window.location.href = buildFlowUrl("insurance"))}
        >
          Insurance Offers
        </OwnliButton>
        <OwnliButton
          variant="secondary"
          onClick={() => (window.location.href = buildFlowUrl("warranty"))}
        >
          Extended Warranty Offers
        </OwnliButton>
        <OwnliButton
          variant="outline"
          onClick={() => (window.location.href = "https://www.ownli.app")}
        >
          Other offers at Ownli
        </OwnliButton>
      </div>
    </div>
  );
};

export default RefinancingOfferCheckScreen;
